import React from 'react'
import CodeMirror from '@uiw/react-codemirror'
import { python } from '@codemirror/lang-python'
import { indentUnit } from '@codemirror/language'
import { FileText, Cat, HelpCircle, Play, Loader2, MousePointer2 } from 'lucide-react'
import Markdown from '../components/Markdown'
import TaskObjectives from '../components/TaskObjectives'
import '../../css/chi.scss'
import '../components/study-ide.scss'
import './study-replay.scss'

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`

// The SAME editor component and props as the live IDE, only not editable.
//
// Previously this hand-rolled an EditorView with codemirror's `basicSetup` and no theme,
// so it rendered light-on-white inside the dark chi-editor shell and with different
// gutters and font metrics — a lookalike that didn't look alike. Using the identical
// component means it can't drift: change the live editor's theme and this follows.
function ReplayEditor ({ code }) {
  return (
    <CodeMirror
      value={code}
      theme="dark"
      height="100%"
      editable={false}
      extensions={[python(), indentUnit.of('    ')]}
    />
  )
}

// The /study task screen rebuilt as a dummy: same chi.scss shell and same markup as
// StudyIDE, every value supplied by the replay state instead of by interaction.
export default function StudyReplayIDE ({ state, task, step, remaining, cursor = true }) {
  const aiAvailable = !!step?.aiAvailable
  const kind = step?.type === 'transfer'
    ? 'Transfer Task'
    : `Learning Task ${step?.ordinal || ''}`

  const RailItem = ({ label, Icon }) => (
    <div className="chi-rail__item is-static"><Icon size={20} strokeWidth={1.8} /><span>{label}</span></div>
  )

  const warn = remaining != null && remaining <= 120 && remaining > 0

  return (
    <div className="chi-root study-chi study-replay-ide">
      {cursor && (
        <div className="study-replay-cursor" style={{ left: `${state.mousePos.x * 100}%`, top: `${state.mousePos.y * 100}%` }}>
          <MousePointer2 size={22} fill={state.isClicking ? '#ef4444' : '#111'} color="#fff" />
          {state.isClicking && <span className="study-replay-cursor__ping" />}
        </div>
      )}

      {!state.tabVisible && (
        <div className="study-replay-away">Tab hidden — participant switched away</div>
      )}

      <header className="chi-topbar">
        <div className="chi-topbar__back study-chi__kind">{kind}</div>
        <div className="chi-topbar__center study-chi__pos">{step?.stepId}</div>
        <div className="chi-topbar__right">
          {remaining != null && <span className={`study-chi__timer${warn ? ' warn' : ''}`}>{fmt(remaining)}</span>}
        </div>
      </header>

      <div className="chi-body">
        <section
          className="chi-desc"
          style={state.leftWidth != null ? { width: `${state.leftWidth}px`, flex: '0 0 auto' } : undefined}
        >
          <nav className="chi-rail">
            <RailItem label="DESC" Icon={FileText} />
            {aiAvailable && <RailItem label="COSMO" Icon={Cat} />}
          </nav>

          {/* Mirrors the live IDE: doc and tutor stacked in one column, no tabs.
              state.leftTab is still tracked by the reducer for older sessions, but it
              no longer chooses what is rendered — both panels are always on screen. */}
          <div className="study-chi__left">
            <div className="chi-desc__content">
              <TaskObjectives domain={task?.domain} />
              <Markdown text={task?.descriptionMd || ''} />
            </div>
            {aiAvailable && (
              <>
                <div className="study-chi__chat-resizer" />
                <div className="study-chi__cosmo">
                  <div className="study-chi__cosmo-head">AI Assistant</div>
                  <div className="cosmo">
                    <div className="cosmo__log">
                      {state.messages.map((m, i) => (
                        <div key={i} className={`cosmo__msg cosmo__msg--${m.role}`}>
                          <div className="cosmo__bubble">{m.text}</div>
                        </div>
                      ))}
                      {state.isAiProcessing && (
                        <div className="cosmo__msg cosmo__msg--assistant">
                          <div className="cosmo__bubble cosmo__typing"><span /><span /><span /></div>
                        </div>
                      )}
                    </div>
                    <div className="cosmo__composer">
                      <input className="cosmo__input" readOnly value={state.chatInput} placeholder="Ask Cosmo…" />
                      <button className="cosmo__send" disabled>Send</button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>

        <div className="chi-divider" />

        <section className="chi-editor-side">
          <div className="chi-ed-tabs">
            <div className="chi-ed-tab">main.py</div>
            <div className="chi-ed-runtime"><span>Python 3 · Trio</span></div>
          </div>
          <div className="chi-editor"><ReplayEditor code={state.code} /></div>
          <div className="chi-console-resizer" />
          <div className="chi-console" style={state.terminalHeight != null ? { height: `${state.terminalHeight}px` } : undefined}>
            <div className="chi-console__tabs">
              <div className={`chi-console__tab${state.consoleTab === 'output' ? ' is-active' : ''}`}>OUTPUT</div>
              <div className={`chi-console__tab${state.consoleTab === 'terminal' ? ' is-active' : ''}`}>TERMINAL</div>
              <div className="chi-console__spacer" />
              <div className="chi-console__hint"><HelpCircle size={14} /><span>Compare against the Expected Output</span></div>
              <button className="chi-run" disabled>
                {state.isRunning ? <Loader2 size={13} className="chi-spin" /> : <Play size={13} fill="#fff" />}
                <span>{state.isRunning ? 'RUNNING' : 'RUN'}</span>
              </button>
            </div>
            <div className="chi-console__body">
              {state.consoleTab === 'terminal'
                ? <><b>/usercode/FILESYSTEM</b>$</>
                : <pre className="chi-output">{state.output || 'Press RUN to execute your code.'}</pre>}
            </div>
          </div>
        </section>
      </div>

      <footer className="chi-bottombar">
        <div className="chi-more">{aiAvailable ? 'AI tutor available for this task' : 'No AI · verify against the Expected Output'}</div>
        <div className="chi-bottombar__right">
          <button className="chi-btn chi-btn--submit" disabled>
            {state.outcome === 'TASK_COMPLETED' ? 'SUBMITTED' : state.outcome === 'TASK_TIMEOUT' ? 'TIMED OUT' : 'SUBMIT'}
          </button>
        </div>
      </footer>
    </div>
  )
}
